import type { ComponentRenderProps } from "@json-render/react";
import { z } from "zod";

import {
  parseReportSpacing,
  parseReportTextStyle,
  REPORT_SPACING_DESCRIPTION,
  REPORT_TEXT_STYLE_DESCRIPTION,
  reportSectionPresentationSchema,
} from "@/components/ui/report/reportPresentation";

import { ReportSection } from "./ReportSection";

const EXAMPLE_ARTICLE_BODY =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.";

/** Layout Studio catalog definition for ReportSection. */
export const ReportSectionSpec = {
  props: z.object({
    ...reportSectionPresentationSchema.shape,
    title: z.string(),
    articles: z.array(
      z.object({
        headline: z.string(),
        sourceName: z.string(),
        sourceUrl: z.string(),
        body: z.string(),
      }),
    ),
  }),
  description: `Topic section for report body content: full-width section label plus story blocks (headline, linked source name, summary body). Each object in articles is one article item, not a section. Repeat ReportSection for each topical section. Default preview: one placeholder article per section unless the user asks for more. Choose descriptive section headings and bracketed placeholders for missing article content; use https://example.com/ for placeholder sourceUrl values. Each article needs headline, sourceName, sourceUrl (hyperlink for the source), and body (Markdown paragraphs, emphasis or lists). Prefer theme primary for the section label — set backgroundColor only for a one-off override. To remove one article item when refining, replace this element with a shorter articles array; do not delete the whole ReportSection unless the user asked to remove the section. Optional presentation (omit for the default look): labelTreatment bar (default filled bar with white label), underline (accent-colored label over an accent rule), or plain (accent-colored label only); backgroundColor is the accent for every treatment. labelStyle, headlineStyle, and bodyStyle set typography for the section label, every article headline, and every article body (${REPORT_TEXT_STYLE_DESCRIPTION}). spacing sets section padding and the gap between label and articles (${REPORT_SPACING_DESCRIPTION}).`,
  example: {
    title: "Section 1",
    articles: [
      {
        headline: "Article item 1.1",
        sourceName: "Source name 1.1",
        sourceUrl: "https://example.com/",
        body: EXAMPLE_ARTICLE_BODY,
      },
    ],
  },
} as const;

type ReportSectionPropsFromSpec = {
  title: string;
  articles: Array<{
    headline: string;
    sourceName: string;
    sourceUrl: string;
    body: string;
  }>;
  backgroundColor?: string | null;
  labelTreatment?: unknown;
  labelStyle?: unknown;
  headlineStyle?: unknown;
  bodyStyle?: unknown;
  spacing?: unknown;
  /** Preview-only note; injected at preview render, not in the catalog. */
  caption?: string;
  /** Spec element id for scroll targets; injected at preview render, not in the catalog. */
  scrollTargetKey?: string;
};

/** Adapter for json-render defineRegistry: renders ReportSection from catalog props. */
export const ReportSectionFromProps = ({ element }: ComponentRenderProps) => {
  const {
    title,
    articles,
    backgroundColor,
    labelTreatment,
    labelStyle,
    headlineStyle,
    bodyStyle,
    spacing,
    caption,
    scrollTargetKey,
  } = element.props as ReportSectionPropsFromSpec;
  const labelTreatmentResult =
    reportSectionPresentationSchema.shape.labelTreatment.safeParse(
      labelTreatment,
    );

  return (
    <ReportSection
      title={title}
      articles={articles}
      backgroundColor={backgroundColor ?? undefined}
      labelTreatment={
        labelTreatmentResult.success ? labelTreatmentResult.data : undefined
      }
      labelStyle={parseReportTextStyle(labelStyle)}
      headlineStyle={parseReportTextStyle(headlineStyle)}
      bodyStyle={parseReportTextStyle(bodyStyle)}
      spacing={parseReportSpacing(spacing)}
      caption={caption}
      scrollTargetKey={scrollTargetKey}
    />
  );
};
